import React, { useMemo } from 'react';

import {
  HeaderSearchableSelectMAIQ,
  HeaderSearchableSelectOption,
} from 'app/components/ReusableUIComponents/LowLevelComponents/HeaderSearchableSelectMAIQ';
import { MakeItem } from 'app/components/ReusableUIComponents/LowLevelComponents/makeSearchUtils';

interface HeaderMakeSelectSingleProps {
  multiple?: false;
  makesList: MakeItem[];
  selectedMake: MakeItem | null;
  selectedMakes?: never;
  disabled?: boolean;
  loading?: boolean;
  onSelect: (make: MakeItem) => void;
  onChange?: never;
  isOptionDisabled?: (make: MakeItem) => boolean;
  disabledOptionTooltip?: string;
  className?: string;
  emptyLabel?: string;
}

interface HeaderMakeSelectMultipleProps {
  multiple: true;
  makesList: MakeItem[];
  selectedMake?: never;
  selectedMakes: MakeItem[];
  disabled?: boolean;
  loading?: boolean;
  onSelect?: never;
  /** Called on Apply and when a chip is removed — parent should persist. */
  onChange: (makes: MakeItem[]) => void;
  isOptionDisabled?: (make: MakeItem) => boolean;
  disabledOptionTooltip?: string;
  className?: string;
  emptyLabel?: string;
}

export type HeaderMakeSelectProps =
  | HeaderMakeSelectSingleProps
  | HeaderMakeSelectMultipleProps;

const toMakeMap = (makes: MakeItem[]): Map<string, MakeItem> => {
  const map = new Map<string, MakeItem>();
  makes.forEach(make => {
    if (make.make_id) {
      map.set(make.make_id, make);
    }
  });
  return map;
};

const toOptions = (makes: MakeItem[]): HeaderSearchableSelectOption[] =>
  makes.map(make => ({
    value: make.make_id,
    label: make.make_name,
    secondaryLabel: make.manufacturer_name,
  }));

export function HeaderMakeSelect(props: HeaderMakeSelectProps) {
  const {
    makesList,
    disabled = false,
    loading = false,
    isOptionDisabled,
    disabledOptionTooltip,
    emptyLabel,
    className,
  } = props;

  const makeById = useMemo(() => toMakeMap(makesList), [makesList]);
  const options = useMemo(() => toOptions(makesList), [makesList]);

  const resolveMakes = (values: string[]): MakeItem[] =>
    values
      .map(value => makeById.get(value))
      .filter((make): make is MakeItem => Boolean(make));

  const isOptionDisabledByOption = isOptionDisabled
    ? (option: HeaderSearchableSelectOption) => {
        const make = makeById.get(option.value);
        return make ? isOptionDisabled(make) : true;
      }
    : undefined;

  if (props.multiple === true) {
    const { onChange, selectedMakes } = props;
    return (
      <HeaderSearchableSelectMAIQ
        multiple
        id="header-make-select"
        className={className}
        options={options}
        values={(selectedMakes || []).map(make => make.make_id)}
        onChange={values => onChange(resolveMakes(values))}
        disabled={disabled}
        loading={loading}
        loadingLabel="Loading makes..."
        emptyLabel={emptyLabel || 'Select makes'}
        searchPlaceholder="Search makes..."
        emptySearchLabel="No makes found"
        emptyOptionsLabel="No makes available"
        emptySelectionLabel="No makes selected"
        isOptionDisabled={isOptionDisabledByOption}
        disabledOptionTooltip={disabledOptionTooltip}
        aria-label="Select makes"
      />
    );
  }

  const { onSelect, selectedMake } = props;
  return (
    <HeaderSearchableSelectMAIQ
      id="header-make-select"
      className={className}
      options={options}
      value={selectedMake?.make_id || ''}
      onChange={value => {
        const make = makeById.get(value);
        if (make) {
          onSelect(make);
        }
      }}
      disabled={disabled}
      loading={loading}
      loadingLabel="Loading makes..."
      emptyLabel={emptyLabel || 'Select make'}
      searchPlaceholder="Search makes..."
      emptySearchLabel="No makes found"
      emptyOptionsLabel="No makes available"
      isOptionDisabled={isOptionDisabledByOption}
      disabledOptionTooltip={disabledOptionTooltip}
      aria-label="Select make"
    />
  );
}

export default HeaderMakeSelect;
